import { queryOptions, useQuery } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";

import { CHAT_TEXTS as TEXTS } from "./texts.ts";

/**
 * Carga de datasets por id (seccion 9, regla de oro).
 *
 * Los componentes NO reciben los numeros por props: reciben un `datasetId` y
 * los piden aqui. Asi lo que se dibuja viene siempre del cálculo del backend,
 * y el modelo no tiene forma de transcribir una cifra equivocada.
 *
 * `get_dataset` es `internal: true` (igual que las de conversaciones) y no
 * declara `output`, así que el OpenAPI generado describe su respuesta como
 * `unknown`. Lo que sigue es un espejo a mano de `DatasetView`/`Column`/`Row`
 * (`datasets/dataset/application/queries/get-dataset/get-dataset.query.ts` y
 * `domain/value-objects/column.ts` del backend), verificado leyendo ese
 * código, no inventado.
 */
export interface DatasetColumn {
  readonly key: string;
  readonly label: string;
  readonly type: "text" | "number" | "date" | "boolean";
}

export type DatasetRow = Readonly<
  Record<string, string | number | boolean | null>
>;

export interface DatasetPayload {
  readonly id: string;
  readonly name: string;
  readonly columns: readonly DatasetColumn[];
  readonly rows: readonly DatasetRow[];
  readonly rowCount: number;
  readonly partial: boolean;
  readonly source: { tool: string; input?: Record<string, unknown> } | null;
  readonly createdAt: string;
}

/**
 * La consulta de un dataset.
 *
 * `queryOptions` empareja la clave con la función que trae los datos, así que
 * no hay forma de pedir una cosa con la clave de otra. Antes esto era un `Map`
 * a mano: la caché, el estado de carga y el error los pone ahora Query.
 *
 * `get_dataset` no infiere el usuario de la sesión (es `internal: true`): hay
 * que mandar `userId` explícito, igual que `conversationId` en el historial.
 */
export const datasetQuery = (datasetId: string, userId: string) =>
  queryOptions({
    queryKey: ["dataset", datasetId, userId] as const,
    queryFn: () => {
      const params = new URLSearchParams({ datasetId, userId });
      return request<DatasetPayload>(
        `${API_BASE}/queries/get_dataset?${params.toString()}`,
      );
    },
    enabled: userId !== "",
    /**
     * Un dataset es INMUTABLE: se produce una vez y caduca a los siete días.
     * No hay nada que refrescar, así que no se vuelve a pedir en toda la
     * sesión aunque se dibuje en una tabla y en un gráfico.
     */
    staleTime: Infinity,
  });

export const useDataset = (datasetId: string, userId: string) =>
  useQuery(datasetQuery(datasetId, userId));

/** Formato de celda segun el tipo declarado por el backend. */
export const formatCell = (
  value: string | number | boolean | null,
  type: DatasetColumn["type"],
): string => {
  if (value === null) return "—";
  if (type === "boolean")
    return value === true ? TEXTS.value.yes : TEXTS.value.no;
  if (type === "number" && typeof value === "number") {
    return value.toLocaleString("es", { maximumFractionDigits: 2 });
  }
  return String(value);
};
